import type { ClientStage, UpdateClientProfileInput } from 'src/gql/graphql';
import { Spinner } from '@savvy-kit/components/spinner';
import { graphql } from 'src/gql';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { CLIENT_STAGE_FULLY_ONBOARDED } from '@savvy/constants/client-stages';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import IntroDoormat from '../guided-flow/intro-doormat';

const UPDATE_CLIENT_PROFILE = graphql(/* GraphQL */ `
  mutation UpdateClientProfileFromPortfolioSplashScreen($updateInput: UpdateClientProfileInput!) {
    updateClientProfile(input: $updateInput) {
      clientProfile {
        id
        stage
      }
    }
  }
`);

const CLIENT_QUERY = graphql(/* GraphQL */ `
  query ClientFromPortfolioSplashScreen {
    client {
      clientProfileId
      user {
        firstName
        lastName
        profilePicUrl
      }
      assignedAdvisor {
        user {
          firstName
          lastName
          email
          profilePicUrl
        }
      }
    }
  }
`);

type UpdateClientProfileWithGeneratedStageInput = Omit<UpdateClientProfileInput, 'stage'> & {
  readonly stage?: ClientStage | null;
};

type PortfolioSplashScreenProps = {
  readonly onDismiss?: () => void;
  readonly onAdvanced?: () => void;
};

export default function PortfolioSplashScreen({ onDismiss, onAdvanced }: PortfolioSplashScreenProps) {
  const [updateClientProfile] = useMutationWithErrorLogging(UPDATE_CLIENT_PROFILE);
  const { data: clientData, loading: clientLoading } = useQueryWithErrorLogging(CLIENT_QUERY);
  const { user: authUser, update } = useAuth();
  async function updateClientProfileField(updateInput: UpdateClientProfileWithGeneratedStageInput) {
    try {
      const res = await updateClientProfile({
        variables: {
          updateInput,
        },
      });

      await update({
        ...authUser,
        clientStage: res.data?.updateClientProfile?.clientProfile.stage,
      });
      onAdvanced?.();
    } catch (error) {
      handleMutationError(error, 'We could not open your dashboard. Please try again.');
    }
  }

  if (clientLoading) {
    return (
      <div className="my-4 flex flex-grow justify-center">
        <Spinner className="size-12" />
      </div>
    );
  }

  const client = clientData?.client;

  return (
    <IntroDoormat
      header="Congratulations!"
      body="Your account setup and onboarding is complete! We’re excited to have you on board as a Savvy client."
      buttonText="Unveil your dashboard"
      secondaryButtonText={onDismiss === undefined ? undefined : 'Skip for now'}
      onSecondary={onDismiss}
      onContinue={() =>
        updateClientProfileField({
          // @ts-expect-error Client query exposes clientProfileId as nullable.
          id: client?.clientProfileId,
          stage: CLIENT_STAGE_FULLY_ONBOARDED,
        })
      }
    />
  );
}
